// AB#1818 — TanStack Query v5 hooks for the Super Admin Categories page.
// Reads via useQuery; writes via useMutation. Every write invalidates the
// admin categories cache so list-tab counters and rows stay consistent.
//
// AB#1872 — approval/request hooks removed (approve/reject/delete). The
// Archive/Restore lifecycle is renamed to Deactivate/Reactivate against the
// canonical `/deactivate` + `/reactivate` routes (backend handoff §AB#1872).
// AB#1871 — adds the lazy, paginated impact drill-down query hooks.
//
// No direct fetch in components — pages consume these hooks only.

import {
    useQuery,
    useInfiniteQuery,
    useMutation,
    useQueryClient,
} from '@tanstack/react-query';
import {
    apiService,
    type AdminCreateCategoryRequest,
    type AdminUpdateCategoryRequest,
} from '../services/api';
import { queryKeys } from '../lib/queryKeys';
import type {
    Category,
    CategoryDeactivateImpact,
    CategoryImpactPage,
    CategoryImpactSalonRow,
    CategoryImpactServiceRow,
    CategoryImpactBookingRow,
    CategoryListResponse,
    RequestCategoryResponse,
} from '../types/api-responses';

// ─────────────────────────────────────────────────────────────────────────────
// Reads
// ─────────────────────────────────────────────────────────────────────────────

/**
 * GET /api/Category/admin/all — full list (deactivated included).
 * SuperAdmin-only on the server; the `<RequireRole>` route guard prevents
 * non-SA callers from ever issuing the request.
 */
export function useAdminCategories() {
    return useQuery<CategoryListResponse>({
        queryKey: queryKeys.adminCategories.list(),
        queryFn: () => apiService.getAllCategoriesAdmin(),
        staleTime: 30_000,
    });
}

// ─────────────────────────────────────────────────────────────────────────────
// Writes — each invalidates `['admin', 'categories']` so the table refetches
// ─────────────────────────────────────────────────────────────────────────────

function useInvalidateAdminCategories() {
    const qc = useQueryClient();
    return () => qc.invalidateQueries({ queryKey: queryKeys.adminCategories.all });
}

/**
 * Create a new SuperAdmin category. Server auto-activates SuperAdmin-created
 * rows (no approval workflow — AB#1872) and honours `isActive` + `targetGender`.
 */
export function useCreateAdminCategory() {
    const invalidate = useInvalidateAdminCategories();
    return useMutation<RequestCategoryResponse, Error, AdminCreateCategoryRequest>({
        mutationFn: (data) => apiService.adminCreateCategory(data),
        onSuccess: () => invalidate(),
    });
}

/**
 * Edit a master category via `PUT /api/Category/admin/{id}` (multipart).
 * The `image` field is sent only when the SuperAdmin picks a new file; omitting
 * it preserves the existing image. `targetGender` is sent through. Invalidates
 * the admin categories cache on success so the list + tab counters refetch.
 */
export function useUpdateAdminCategory() {
    const invalidate = useInvalidateAdminCategories();
    return useMutation<
        Category,
        Error,
        { categoryId: number; data: AdminUpdateCategoryRequest }
    >({
        mutationFn: ({ categoryId, data }) =>
            apiService.adminUpdateCategory(categoryId, data),
        onSuccess: () => invalidate(),
    });
}

// ─────────────────────────────────────────────────────────────────────────────
// AB#1871 — Deactivate-impact: counts + lazy paginated drill-down
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Pre-deactivate blast-radius counts (N/M/K/X) via
 * `GET /api/Category/admin/{id}/impact`. Disabled until a `categoryId` is set
 * (i.e. the deactivate dialog is opened for a row). Point-in-time at dialog-open
 * — `staleTime: 0` ensures fresh counts every time the dialog reopens.
 */
export function useCategoryImpact(categoryId: number | null) {
    return useQuery<CategoryDeactivateImpact>({
        queryKey: queryKeys.adminCategories.impact(categoryId ?? -1),
        queryFn: () => apiService.adminGetCategoryImpact(categoryId as number),
        enabled: categoryId != null,
        staleTime: 0,
        gcTime: 0,
        retry: false,
    });
}

/** Server clamps `take` to ≤100; we page in fixed windows below the cap. */
const IMPACT_PAGE_SIZE = 25;

/**
 * AB#1871 — lazy, paginated drill-down of the affected salons. Only fetches
 * when `enabled` (the accordion is expanded). `+ N more` loads the next page.
 */
export function useCategoryImpactSalons(categoryId: number | null, enabled: boolean) {
    return useInfiniteQuery<CategoryImpactPage<CategoryImpactSalonRow>>({
        queryKey: queryKeys.adminCategories.impactSalons(categoryId ?? -1),
        queryFn: ({ pageParam }) =>
            apiService.adminGetCategoryImpactSalons(
                categoryId as number,
                pageParam as number,
                IMPACT_PAGE_SIZE,
            ),
        initialPageParam: 0,
        getNextPageParam: (last) => {
            const loaded = last.skip + last.items.length;
            return loaded < last.total ? loaded : undefined;
        },
        enabled: categoryId != null && enabled,
        staleTime: 0,
        gcTime: 0,
        retry: false,
    });
}

/**
 * AB#1871 — lazy, paginated drill-down of the affected services. Rows carry
 * `fromPrice` + ISO-4217 `currency` (rendered "From {price} {JOD|AED}").
 */
export function useCategoryImpactServices(categoryId: number | null, enabled: boolean) {
    return useInfiniteQuery<CategoryImpactPage<CategoryImpactServiceRow>>({
        queryKey: queryKeys.adminCategories.impactServices(categoryId ?? -1),
        queryFn: ({ pageParam }) =>
            apiService.adminGetCategoryImpactServices(
                categoryId as number,
                pageParam as number,
                IMPACT_PAGE_SIZE,
            ),
        initialPageParam: 0,
        getNextPageParam: (last) => {
            const loaded = last.skip + last.items.length;
            return loaded < last.total ? loaded : undefined;
        },
        enabled: categoryId != null && enabled,
        staleTime: 0,
        gcTime: 0,
        retry: false,
    });
}

/**
 * AB#1871 — lazy, paginated drill-down of upcoming bookings. PDPL-SAFE: the
 * rows carry date/time + service + salon + status ONLY (no customer identity).
 */
export function useCategoryImpactBookings(categoryId: number | null, enabled: boolean) {
    return useInfiniteQuery<CategoryImpactPage<CategoryImpactBookingRow>>({
        queryKey: queryKeys.adminCategories.impactBookings(categoryId ?? -1),
        queryFn: ({ pageParam }) =>
            apiService.adminGetCategoryImpactBookings(
                categoryId as number,
                pageParam as number,
                IMPACT_PAGE_SIZE,
            ),
        initialPageParam: 0,
        getNextPageParam: (last) => {
            const loaded = last.skip + last.items.length;
            return loaded < last.total ? loaded : undefined;
        },
        enabled: categoryId != null && enabled,
        staleTime: 0,
        gcTime: 0,
        retry: false,
    });
}

// ─────────────────────────────────────────────────────────────────────────────
// AB#1872 — Deactivate / Reactivate lifecycle (renamed from Archive / Restore)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Deactivate a category via `POST /api/Category/admin/{id}/deactivate` (soft,
 * reversible; fresh `X-Idempotency-Key` per call). Invalidates the admin
 * categories cache so the row moves to the Deactivated tab.
 */
export function useDeactivateCategory() {
    const invalidate = useInvalidateAdminCategories();
    return useMutation<boolean, Error, number>({
        mutationFn: (categoryId) => apiService.adminDeactivateCategory(categoryId),
        onSuccess: () => invalidate(),
    });
}

/**
 * Reactivate a category via `POST /api/Category/admin/{id}/reactivate`
 * (idempotent; fresh `X-Idempotency-Key` per call). Invalidates the admin
 * categories cache so the row leaves the Deactivated tab and re-publishes.
 */
export function useReactivateCategory() {
    const invalidate = useInvalidateAdminCategories();
    return useMutation<boolean, Error, number>({
        mutationFn: (categoryId) => apiService.adminReactivateCategory(categoryId),
        onSuccess: () => invalidate(),
    });
}
